/**
 * InstallPrompt — Bandeau d'invitation a l'installation PWA.
 *
 * - Detecte si l'app est deja installee (standalone) -> ne s'affiche pas
 * - Detecte iOS vs Android pour adapter le message
 * - iOS : instructions visuelles (Apple n'autorise pas l'install programmatique)
 * - Android : bouton 'Installer' qui declenche beforeinstallprompt
 * - Dismissable, etat persiste dans localStorage (s1_install_prompt_dismissed)
 * - Ne s'affiche qu'a partir du 2e login pour ne pas spammer
 */

import { useCallback, useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY_DISMISSED = "s1_install_prompt_dismissed";
const STORAGE_KEY_VISIT_COUNT = "s1_install_prompt_visit_count";
const MIN_VISITS_BEFORE_SHOW = 2;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari (propriete non-standard exposee par WebKit)
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

function isIOS(): boolean {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) && !/Windows|Android/i.test(ua);
}

function incrementVisitCount(): number {
  try {
    const current = parseInt(localStorage.getItem(STORAGE_KEY_VISIT_COUNT) ?? "0", 10);
    const next = Number.isFinite(current) ? current + 1 : 1;
    localStorage.setItem(STORAGE_KEY_VISIT_COUNT, String(next));
    return next;
  } catch {
    return 0;
  }
}

function isDismissed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_DISMISSED) === "true";
  } catch {
    return false;
  }
}

function persistDismissed(): void {
  try {
    localStorage.setItem(STORAGE_KEY_DISMISSED, "true");
  } catch {
    // localStorage indisponible (mode prive) — on ignore
  }
}

export function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (isDismissed()) return;

    const visits = incrementVisitCount();
    if (visits < MIN_VISITS_BEFORE_SHOW) return;

    setIsIOSDevice(isIOS());

    // Android : on attend l'evenement beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
      setShow(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // iOS : pas d'evenement systeme, on affiche directement les instructions
    if (isIOS()) {
      setShow(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    const result = await installEvent.userChoice;
    if (result.outcome === "accepted") {
      setShow(false);
      persistDismissed();
    }
  }, [installEvent]);

  const handleDismiss = useCallback(() => {
    setShow(false);
    persistDismissed();
  }, []);

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-label="Installer Systeme1"
      className="fixed left-3 right-3 bottom-[calc(5rem+env(safe-area-inset-bottom))] md:left-auto md:right-6 md:bottom-6 md:w-[360px] z-40 rounded-xl border border-border bg-card/95 backdrop-blur shadow-2xl p-4"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center">
          <Download className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold leading-tight">Installer Systeme1</div>
          {isIOSDevice ? (
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
              Appuie sur <Share className="inline h-3 w-3 align-text-bottom" />{" "}
              puis <strong>Sur l'ecran d'accueil</strong> pour installer l'app.
            </p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
              Acces direct depuis ton ecran d'accueil, notifications push, mode plein ecran.
            </p>
          )}
          {!isIOSDevice && installEvent && (
            <Button
              size="sm"
              onClick={() => void handleInstall()}
              className="mt-3 h-8 text-xs"
            >
              Installer maintenant
            </Button>
          )}
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Fermer"
          className="shrink-0 h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
